import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gear, Plus, Trash, Tag, Globe, MapPin, PencilSimple, Check, X, MagnifyingGlass, Cube, CheckCircle, PauseCircle, PencilLine, ProhibitInset, Buildings, UsersThree, Stack, MagnifyingGlass as Search } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { COUNTRIES } from "@/lib/countries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

/* ------------------------------ Categories ------------------------------ */
function CategoriesConfig() {
  const [cats, setCats] = useState(null);
  const [name, setName] = useState("");
  const load = () => api.get("/admin/config/categories").then((r) => setCats(r.data)).catch(() => setCats([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/admin/config/categories", { name }); toast.success("Category added"); setName(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (c) => {
    if (!window.confirm(`Remove "${c.name}"? New registrations won't see it.`)) return;
    try { await api.delete(`/admin/config/categories/${c.id}`); toast.success("Category removed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="card-surface p-5" data-testid="categories-config">
        <p className="font-display font-semibold mb-3 flex items-center gap-2"><Tag size={18} className="text-primary" /> Business categories</p>
        <div className="flex gap-2 mb-4">
          <Input data-testid="category-name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="e.g. Bakery" className="bg-muted/40 border-black/10 h-11 rounded-xl" />
          <Button data-testid="add-category" onClick={add} className="h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shrink-0"><Plus size={16} className="mr-1" /> Add</Button>
        </div>
        <div className="divide-y divide-black/5" data-testid="category-list">
          {!cats && <p className="text-sm text-muted-foreground py-2">Loading…</p>}
          {cats && cats.length === 0 && <p className="text-sm text-muted-foreground py-2">No categories yet.</p>}
          {cats && cats.map((c) => (
            <div key={c.id} data-testid={`category-${c.id}`} className="flex items-center justify-between py-2.5">
              <span className="text-sm font-medium">{c.name}</span>
              <button data-testid={`delete-category-${c.id}`} onClick={() => del(c)} className="text-muted-foreground hover:text-destructive p-1.5"><Trash size={17} /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="card-surface p-5">
        <p className="font-display font-semibold mb-3 flex items-center gap-2"><Globe size={18} className="text-primary" /> Supported countries</p>
        <div className="flex flex-wrap gap-2">
          {COUNTRIES.map((c) => (
            <span key={c.code} className="flex items-center gap-2 rounded-full border border-black/10 bg-muted/40 px-3 py-1.5 text-sm">
              <span className="text-base leading-none">{c.flag}</span> {c.name} <span className="text-muted-foreground text-xs">{c.dial}</span>
            </span>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-3">These appear in the country selector during registration.</p>
      </div>
    </div>
  );
}

/* -------------------------------- Cities -------------------------------- */
function CitiesConfig() {
  const [cities, setCities] = useState(null);
  const [name, setName] = useState("");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);

  const load = (q = "") => api.get("/admin/config/cities", { params: q ? { q } : {} })
    .then((r) => setCities(r.data)).catch(() => setCities([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(search), 300); return () => clearTimeout(t); }, [search]);

  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/admin/config/cities", { name, country: "Tanzania" }); toast.success("City added"); setName(""); load(search); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveEdit = async () => {
    if (!editing.name.trim()) return;
    try { await api.patch(`/admin/config/cities/${editing.id}`, { name: editing.name }); toast.success("City updated"); setEditing(null); load(search); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggle = async (c) => {
    try { await api.patch(`/admin/config/cities/${c.id}`, { active: !c.active }); toast.success(c.active ? "City deactivated" : "City activated"); load(search); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const fmtDate = (s) => s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";

  return (
    <div className="card-surface p-5 max-w-2xl" data-testid="cities-config">
      <p className="font-display font-semibold mb-1 flex items-center gap-2"><MapPin size={18} className="text-primary" /> Cities</p>
      <p className="text-xs text-muted-foreground mb-3">Only <span className="font-medium text-foreground">active</span> cities appear in business and branch location dropdowns. Currently configured for Tanzania.</p>
      <div className="flex gap-2 mb-3">
        <Input data-testid="city-name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="e.g. Dar es Salaam" className="bg-muted/40 border-black/10 h-11 rounded-xl" />
        <Button data-testid="add-city" onClick={add} className="h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shrink-0"><Plus size={16} className="mr-1" /> Add</Button>
      </div>
      <div className="relative mb-3">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="city-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search cities…" className="pl-9 bg-muted/40 border-black/10 h-10 rounded-xl" />
      </div>
      <div className="divide-y divide-black/5" data-testid="city-list">
        {!cities && <p className="text-sm text-muted-foreground py-2">Loading…</p>}
        {cities && cities.length === 0 && <p className="text-sm text-muted-foreground py-2" data-testid="city-empty">No cities found.</p>}
        {cities && cities.map((c) => (
          <div key={c.id} data-testid={`city-${c.id}`} className="flex items-center justify-between gap-3 py-2.5">
            {editing?.id === c.id ? (
              <div className="flex items-center gap-2 flex-1">
                <Input data-testid={`edit-city-input-${c.id}`} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && saveEdit()} className="h-9 bg-muted/40 border-black/10 rounded-lg" />
                <button data-testid={`save-city-${c.id}`} onClick={saveEdit} className="text-primary p-1.5"><Check size={18} /></button>
                <button onClick={() => setEditing(null)} className="text-muted-foreground p-1.5"><X size={18} /></button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-medium truncate">{c.name}</span>
                  <span data-testid={`city-status-${c.id}`} className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full shrink-0 ${c.active ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>{c.active ? "Active" : "Inactive"}</span>
                  <span className="text-[11px] text-muted-foreground shrink-0 hidden sm:inline">· {c.country} · Created {fmtDate(c.created_at)}{c.updated_at && c.updated_at !== c.created_at ? ` · Updated ${fmtDate(c.updated_at)}` : ""}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button data-testid={`edit-city-${c.id}`} onClick={() => setEditing({ id: c.id, name: c.name })} className="text-muted-foreground hover:text-foreground p-1.5"><PencilSimple size={17} /></button>
                  <button data-testid={`toggle-city-${c.id}`} onClick={() => toggle(c)} className={`text-xs font-semibold rounded-full px-3 py-1.5 ${c.active ? "text-muted-foreground hover:bg-muted" : "text-primary hover:bg-primary/10"}`}>{c.active ? "Deactivate" : "Activate"}</button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* --------------------------- Loyalty modules ---------------------------- */
const STATE_STYLES = {
  active: "bg-emerald-100 text-emerald-700",
  paused: "bg-amber-100 text-amber-700",
  draft: "bg-muted text-muted-foreground",
  retired: "bg-red-100 text-red-700",
};
const STATE_ACTIONS = [
  { state: "active", label: "Active", icon: CheckCircle },
  { state: "paused", label: "Paused", icon: PauseCircle },
  { state: "draft", label: "Draft", icon: PencilLine },
  { state: "retired", label: "Retired", icon: ProhibitInset },
];
const SCOPES = [
  { key: "all", label: "All businesses", icon: Globe, hint: "Every merchant on the platform" },
  { key: "categories", label: "Specific categories", icon: Tag, hint: "Merchants in the chosen categories" },
  { key: "businesses", label: "Specific businesses", icon: Buildings, hint: "Only the selected merchants" },
];

function scopeSummary(m) {
  if (m.state !== "active") return "Not enabled";
  if (m.scope === "categories") { const n = (m.categories || []).length; return `${n} categor${n === 1 ? "y" : "ies"}`; }
  if (m.scope === "businesses") { const n = (m.businesses || []).length; return `${n} business${n === 1 ? "" : "es"}`; }
  return "All businesses";
}

const MODULE_DESCRIPTIONS = {
  point_currencies: "Define point currencies, wallets & expiry.",
  purchase_rewards: "Earn points from spend-based purchases.",
  visit_stamp_rewards: "Stamp cards & visit-based rewards.",
  eligibility_rules: "Who qualifies — private & targeted rules.",
  stacking_rules: "Combine bonuses & multipliers safely.",
  segments: "Group customers for targeted actions.",
  campaigns: "Run offers with holdout measurement.",
  notifications: "Push, SMS & in-app messaging.",
  referrals: "Reward customers for inviting friends.",
  milestones_streaks: "Streaks & milestone achievements.",
  memberships: "Bronze/Silver/Gold tiers & multipliers.",
  connected_programs: "Cross-merchant point exchange.",
  fraud_controls: "Risk review queues & blocking.",
  analytics: "KPI dashboards & performance metrics.",
  recommendations: "AI-driven next-best-reward engine.",
};

function ModuleConfigDialog({ mod, categories, businesses, onClose, onSaved }) {
  const [state, setState] = useState(mod.state || "draft");
  const [scope, setScope] = useState(mod.scope || "all");
  const [cats, setCats] = useState(new Set(mod.categories || []));
  const [bizs, setBizs] = useState(new Set(mod.businesses || []));
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  const toggleSet = (setter) => (val) => setter((prev) => {
    const next = new Set(prev); next.has(val) ? next.delete(val) : next.add(val); return next;
  });
  const filteredBiz = businesses.filter((b) => b.name.toLowerCase().includes(q.toLowerCase()));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/modules/${mod.key}`, {
        state, scope,
        categories: Array.from(cats),
        businesses: Array.from(bizs),
      });
      toast.success(`${mod.name} updated`);
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid={`module-dialog-${mod.key}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Cube size={20} className="text-primary" weight="duotone" /> {mod.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Status</p>
            <div className="flex flex-wrap gap-1.5">
              {STATE_ACTIONS.map((a) => (
                <button key={a.state} data-testid={`module-state-${a.state}`} onClick={() => setState(a.state)}
                  className={`inline-flex items-center gap-1 text-xs font-medium rounded-full px-3 py-1.5 transition-colors ${state === a.state ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-black/5"}`}>
                  <a.icon size={14} /> {a.label}
                </button>
              ))}
            </div>
          </div>

          <div className={state === "active" ? "" : "opacity-40 pointer-events-none"}>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Activation level</p>
            <div className="space-y-2">
              {SCOPES.map((s) => (
                <button key={s.key} data-testid={`module-scope-${s.key}`} onClick={() => setScope(s.key)}
                  className={`flex items-center gap-3 w-full text-left rounded-xl border p-3 transition-colors ${scope === s.key ? "border-primary bg-primary/5" : "border-black/10 hover:bg-black/5"}`}>
                  <s.icon size={18} className={scope === s.key ? "text-primary" : "text-muted-foreground"} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{s.label}</p>
                    <p className="text-[11px] text-muted-foreground">{s.hint}</p>
                  </div>
                  <span className={`h-4 w-4 rounded-full border-2 ${scope === s.key ? "border-primary bg-primary" : "border-black/20"}`} />
                </button>
              ))}
            </div>

            {scope === "categories" && (
              <div className="mt-3 space-y-1.5" data-testid="module-categories">
                {categories.length === 0 && <p className="text-xs text-muted-foreground">No categories configured yet.</p>}
                {categories.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm rounded-lg px-2 py-1.5 hover:bg-black/5 cursor-pointer">
                    <input type="checkbox" data-testid={`module-cat-${c.name}`} checked={cats.has(c.name)} onChange={() => toggleSet(setCats)(c.name)} className="accent-primary h-4 w-4" />
                    {c.name}
                  </label>
                ))}
              </div>
            )}

            {scope === "businesses" && (
              <div className="mt-3" data-testid="module-businesses">
                <div className="relative mb-2">
                  <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search businesses…" className="pl-8 h-9 rounded-lg bg-muted/40 border-black/10 text-sm" />
                </div>
                <div className="space-y-1 max-h-44 overflow-y-auto">
                  {filteredBiz.length === 0 && <p className="text-xs text-muted-foreground">No businesses found.</p>}
                  {filteredBiz.map((b) => (
                    <label key={b.id} className="flex items-center gap-2 text-sm rounded-lg px-2 py-1.5 hover:bg-black/5 cursor-pointer">
                      <input type="checkbox" data-testid={`module-biz-${b.id}`} checked={bizs.has(b.id)} onChange={() => toggleSet(setBizs)(b.id)} className="accent-primary h-4 w-4" />
                      <span className="flex-1 truncate">{b.name}</span>
                      <span className="text-[11px] text-muted-foreground">{b.category}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl border-black/10">Cancel</Button>
          <Button data-testid="module-save" onClick={save} disabled={saving} className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
            {saving ? "Saving…" : "Save configuration"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ModulesConfig() {
  const [mods, setMods] = useState(null);
  const [categories, setCategories] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [active, setActive] = useState(null);

  const load = () => api.get("/admin/modules").then((r) => setMods(r.data)).catch(() => setMods([]));
  useEffect(() => {
    load();
    api.get("/admin/config/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/admin/businesses", { params: { limit: 100 } }).then((r) => setBusinesses(r.data.items || [])).catch(() => {});
  }, []);

  if (!mods) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="admin-modules">
      <p className="text-muted-foreground text-sm">
        Decide which capabilities merchants can use and at what level. Only <span className="font-medium text-foreground">active</span> modules can be configured or launched by businesses. Click a module to configure it.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {mods.map((m) => (
          <button key={m.key} data-testid={`module-${m.key}`} onClick={() => setActive(m)}
            className="card-surface p-4 aspect-square flex flex-col text-left transition-transform hover:-translate-y-0.5 hover:shadow-lg">
            <div className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center mb-3">
              <Cube size={20} weight="duotone" className="text-primary" />
            </div>
            <div className="flex-1">
              <p className="font-display font-semibold text-sm leading-tight">{m.name}</p>
              <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{MODULE_DESCRIPTIONS[m.key] || ""}</p>
            </div>
            <div className="mt-2 space-y-1.5">
              <span data-testid={`module-state-${m.key}`} className={`inline-block text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${STATE_STYLES[m.state] || STATE_STYLES.draft}`}>{m.state}</span>
              <p className="text-[11px] text-muted-foreground truncate">{scopeSummary(m)}</p>
            </div>
          </button>
        ))}
      </div>
      {active && (
        <ModuleConfigDialog mod={active} categories={categories} businesses={businesses}
          onClose={() => setActive(null)} onSaved={() => { setActive(null); load(); }} />
      )}
    </div>
  );
}

/* -------------------------------- Page ---------------------------------- */
export default function AdminConfig() {
  return (
    <div className="space-y-6" data-testid="admin-config-page">
      <div>
        <h1 className="font-display font-bold text-3xl flex items-center gap-2"><Gear size={26} className="text-primary" /> Configurations</h1>
        <p className="text-muted-foreground text-sm">Manage platform-wide options: registration choices and loyalty module governance.</p>
      </div>

      <Tabs defaultValue="categories">
        <TabsList data-testid="config-tabs" className="bg-muted/50 rounded-xl">
          <TabsTrigger value="categories" data-testid="tab-categories" className="rounded-lg gap-1.5"><Tag size={15} /> Business categories</TabsTrigger>
          <TabsTrigger value="cities" data-testid="tab-cities" className="rounded-lg gap-1.5"><MapPin size={15} /> Cities</TabsTrigger>
          <TabsTrigger value="modules" data-testid="tab-modules" className="rounded-lg gap-1.5"><Cube size={15} /> Loyalty modules</TabsTrigger>
        </TabsList>
        <TabsContent value="categories" className="mt-5"><CategoriesConfig /></TabsContent>
        <TabsContent value="cities" className="mt-5"><CitiesConfig /></TabsContent>
        <TabsContent value="modules" className="mt-5"><ModulesConfig /></TabsContent>
      </Tabs>
    </div>
  );
}
